import { useCallback, useState } from "react";
import { DateFilterInput, type ResolvedFilter } from "./DateFilterInput";
import type { ParseSubjectsResult } from "./parseSubjects";
import { SubjectInput } from "./SubjectInput";

interface SubjectInputModeTabsProps {
    /**
     * Notifies the parent on every change of the validated subject list
     * (paste/upload mode). Filter mode never emits IDs — see
     * `onFilterChanged`.
     */
    onParsed: (result: ParseSubjectsResult) => void;
    /**
     * PRD-001 rev 13: notifies the parent when the resolved filter changes
     * (filter mode). `null` = no valid filter. Mutually exclusive with
     * `onParsed` emissions — switching modes clears BOTH (a stale filter
     * must never ride along with a pasted list, and vice versa).
     */
    onFilterChanged: (filter: ResolvedFilter | null) => void;
    /** Initial paste text — used to restore drafts on remount. */
    initialPasteText?: string;
    /** Persist paste-mode text drafts. Only fired in paste/upload mode. */
    onPasteTextChange?: (text: string) => void;
    /** Notifies the parent when the active mode changes. */
    onModeChange?: (mode: SubjectInputMode) => void;
}

export type SubjectInputMode = "paste-or-upload" | "date-filter";

const EMPTY_PARSE: ParseSubjectsResult = {
    valid: [],
    duplicateCount: 0,
    invalid: [],
    totalRows: 0,
};

/**
 * Subject-input mode selector (PRD-001 §48/§49). Three input modes are
 * specified, but file-upload and paste live in the same UI block in
 * `SubjectInput` because they share a textarea-as-source-of-truth — so the
 * tabs surface two choices: "Upload subject IDs" vs "Filter". Rev 13: the
 * filter mode emits the FILTER PARAMETERS; subject resolution happens in the
 * job's server-side gathering phase after submission.
 *
 * Modes are mutually exclusive per submission (PRD §48): switching tabs
 * clears the prior mode's input state. The mode-tab component owns the
 * mode state; each child owns its own input state and resets on unmount.
 */
export function SubjectInputModeTabs({
    onParsed,
    onFilterChanged,
    initialPasteText = "",
    onPasteTextChange,
    onModeChange,
}: SubjectInputModeTabsProps) {
    const [mode, setMode] = useState<SubjectInputMode>("paste-or-upload");

    const handleSwitch = useCallback(
        (next: SubjectInputMode) => {
            if (next === mode) return;
            // PRD §48: switching modes clears the prior mode's input.
            // We achieve this structurally by unmounting the prior child
            // (each child holds its own state, which goes away on unmount).
            // Clear BOTH parent-side inputs so neither a stale ID list nor a
            // stale filter survives the switch (panel: general-purpose I5).
            onParsed(EMPTY_PARSE);
            onFilterChanged(null);
            setMode(next);
            onModeChange?.(next);
        },
        [mode, onParsed, onFilterChanged, onModeChange],
    );

    return (
        <div className="subject-input-mode-tabs" data-testid="subject-input-mode-tabs">
            <div
                role="tablist"
                aria-label="Subject input mode"
                style={{ display: "flex", gap: 8, marginBottom: 12 }}
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "paste-or-upload"}
                    className={`btn ${mode === "paste-or-upload" ? "btn-primary" : "btn-secondary"} btn-sm`}
                    onClick={() => handleSwitch("paste-or-upload")}
                    data-testid="subject-mode-paste-or-upload"
                >
                    <i className="fa-solid fa-paste" aria-hidden="true" /> Upload subject IDs
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "date-filter"}
                    className={`btn ${mode === "date-filter" ? "btn-primary" : "btn-secondary"} btn-sm`}
                    onClick={() => handleSwitch("date-filter")}
                    data-testid="subject-mode-date-filter"
                >
                    <i className="fa-solid fa-filter" aria-hidden="true" /> Filter
                </button>
            </div>

            <div className="subject-input-mode-panel">
                {mode === "paste-or-upload" ? (
                    <SubjectInput
                        onParsed={onParsed}
                        initialText={initialPasteText}
                        onTextChange={onPasteTextChange}
                    />
                ) : (
                    <DateFilterInput onFilterChanged={onFilterChanged} />
                )}
            </div>
        </div>
    );
}
